import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Shared shell for every inline-SVG diagram.
 * Diagrams scale to their frame. Dense charts opt into mobile scrolling
 * with a visible edge fade and hint, while still fitting article columns.
 */
export function DiagramFrame({
  caption,
  captionClassName,
  className,
  scrollable = false,
  children,
}: {
  caption?: ReactNode;
  captionClassName?: string;
  className?: string;
  /** Set false for fluid HTML diagrams that need no min-width scroll. */
  scrollable?: boolean;
  children: ReactNode;
}) {
  return (
    <figure className={cn("not-prose w-full min-w-0 max-w-full", className)}>
      <div
        className={cn("diagram-viewport w-full max-w-full overflow-x-auto overscroll-x-contain", scrollable && "diagram-scroll")}
        tabIndex={0}
        role="group"
        aria-label={scrollable ? "Diagram, scrollable on narrow screens" : "Diagram"}
      >
        <div className={scrollable ? "diagram-wide" : undefined}>{children}</div>
      </div>

      {scrollable ? <p className="scroll-hint mt-2 text-center text-xs text-charcoal sm:hidden">Scroll sideways to see the full diagram</p> : null}

      {caption ? (
        <figcaption className={cn(
            "mx-auto mt-5 max-w-2xl text-center text-sm leading-relaxed text-charcoal",
            captionClassName,
          )}>
          {caption}
        </figcaption>
      ) : null}
    </figure>
  );
}

/** Entrance transform for diagram parts: translate only, never opacity. */
export const rise = (reduced: boolean, delay = 0) =>
  reduced
    ? { initial: false as const }
    : {
        initial: { y: 14 },
        whileInView: { y: 0 },
        viewport: { once: true, margin: "-40px" },
        transition: { duration: 0.45, delay },
      };
